import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/shared/AuthContext';
import PageContainer from '../components/common/PageContainer';
import PageHeader from '../components/common/PageHeader';
import PageSection from '../components/common/PageSection';
import AddPatientModal from '../components/therapist/AddPatientModal';
import apiService from '../services/shared/apiService';

interface Patient {
    uid: string;
    displayName: string;
    email: string;
    lastActive?: string;
    needsAttention?: boolean;
    status?: 'Active' | 'Inactive' | 'Needs Attention';
    riskLevel?: 'High' | 'Medium' | 'Low';
    progress?: number;
}

const TherapistPatients: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [patients, setPatients] = useState<Patient[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showMessageModal, setShowMessageModal] = useState(false);
    const [showAddPatientModal, setShowAddPatientModal] = useState(false);

    useEffect(() => {
        loadPatients();
    }, [user]);

    const getRiskColor = (riskLevel: string) => {
        switch (riskLevel) {
            case 'High': return 'text-red-600 bg-red-100';
            case 'Medium': return 'text-yellow-600 bg-yellow-100';
            case 'Low': return 'text-green-600 bg-green-100';
            default: return 'text-gray-600 bg-gray-100';
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Active': return 'text-green-600 bg-green-100';
            case 'Inactive': return 'text-gray-600 bg-gray-100';
            default: return 'text-gray-600 bg-gray-100';
        }
    };


    const handleMessagePatient = (patient: Patient) => {
        setSelectedPatient(patient);
        setShowMessageModal(true);
    };

    const handleAddPatient = () => {
        setShowAddPatientModal(true);
    };

    const handlePatientAdded = () => {
        // Reload patients after adding a new one
        if (user) {
            loadPatients();
        }
    };

    const closeModals = () => {
        setShowViewModal(false);
        setShowMessageModal(false);
        setShowAddPatientModal(false);
        setSelectedPatient(null);
    };

    const loadPatients = async () => {
        if (!user) return;
        
        setIsLoading(true);
        setError(null);
        
        try {
            const response = await apiService.getTherapistPatients(user.uid);
            const realPatients: Patient[] = response.patients.map(patient => ({
                uid: patient.uid,
                displayName: patient.displayName || 'Unknown',
                email: patient.email,
                lastActive: typeof patient.lastActive === 'string' ? patient.lastActive : 
                           typeof patient.lastActive === 'number' ? new Date(patient.lastActive).toISOString() : undefined,
                needsAttention: (patient as any).needsAttention || false,
                status: (patient as any).needsAttention ? 'Needs Attention' : 'Active',
                riskLevel: (patient as any).needsAttention ? 'High' : 'Low',
                progress: Math.floor(Math.random() * 100) // TODO: Get real progress from API
            }));
            
            setPatients(realPatients);
        } catch (err) {
            console.error('Error loading patients:', err);
            setError('Failed to load patient data. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    if (error) {
        return (
            <PageContainer>
                <PageHeader 
                    title="Patient Management" 
                    emoji="👥"
                    description="Manage your patient roster and track progress"
                />
                <div className="text-center py-8">
                    <p className="text-red-500 mb-4">{error}</p>
                    <button 
                        onClick={() => window.location.reload()} 
                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                        Retry
                    </button>
                </div>
            </PageContainer>
        );
    }

    const totalPatients = patients.length;
    const highRiskPatients = patients.filter(p => p.riskLevel === 'High').length;
    const activePatients = patients.filter(p => p.status === 'Active').length;
    const averageProgress = patients.length > 0 ? Math.round(patients.reduce((sum, p) => sum + (p.progress || 0), 0) / patients.length) : 0;

    return (
        <PageContainer>
            <PageHeader 
                title="Patient Management" 
                emoji="👥"
                description="Manage your patient roster and track progress"
            />
            
            {isLoading ? (
                <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2">Loading patients...</p>
                </div>
            ) : (
                <>
                    {/* Patient Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                        <div className="bg-white rounded-lg shadow p-6 text-center">
                            <div className="text-3xl mb-3">👥</div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Total Patients</h3>
                            <div className="text-4xl font-bold text-blue-600 mb-2">{totalPatients}</div>
                            <div className="text-sm text-gray-600">Active Patients</div>
                        </div>
                        
                        <div className="bg-white rounded-lg shadow p-6 text-center">
                            <div className="text-3xl mb-3">🚨</div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">High Risk</h3>
                            <div className="text-4xl font-bold text-red-600 mb-2">{highRiskPatients}</div>
                            <div className="text-sm text-gray-600">Require Attention</div>
                        </div>
                        
                        <div className="bg-white rounded-lg shadow p-6 text-center">
                            <div className="text-3xl mb-3">✅</div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Active</h3>
                            <div className="text-4xl font-bold text-green-600 mb-2">{activePatients}</div>
                            <div className="text-sm text-gray-600">Currently Active</div>
                        </div>
                        
                        <div className="bg-white rounded-lg shadow p-6 text-center">
                            <div className="text-3xl mb-3">📈</div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Avg Progress</h3>
                            <div className="text-4xl font-bold text-purple-600 mb-2">{averageProgress}%</div>
                            <div className="text-sm text-gray-600">Completion Rate</div>
                        </div>
                    </div>

            {/* Patient List */}
            <PageSection title="Patient Roster" emoji="📋">
                <div className="space-y-4">
                    {patients.map((patient) => (
                        <div key={patient.uid} className="p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-4">
                                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                                        <span className="text-xl">👤</span>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-900">{patient.displayName}</h3>
                                        <p className="text-sm text-gray-600">{patient.email}</p>
                                        <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(patient.status || 'Active')}`}>
                                                {patient.status || 'Active'}
                                            </span>
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskColor(patient.riskLevel || 'Low')}`}>
                                                {patient.riskLevel || 'Low'} Risk
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-6">
                                    <div className="text-right">
                                        <div className="text-sm font-medium text-gray-900">Progress</div>
                                        <div className="w-20 bg-gray-200 rounded-full h-2 mt-1">
                                            <div 
                                                className="bg-blue-600 h-2 rounded-full" 
                                                style={{ width: `${patient.progress || 0}%` }}
                                            ></div>
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">{patient.progress || 0}%</div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-sm font-medium text-gray-900">Last Active</div>
                                        <div className="text-xs text-gray-500">
                                            {patient.lastActive ? new Date(patient.lastActive).toLocaleDateString() : 'Unknown'}
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-sm font-medium text-gray-900">Status</div>
                                        <div className="text-xs text-gray-500">
                                            {patient.needsAttention ? 'Needs Attention' : 'Stable'}
                                        </div>
                                    </div>
                                    <div className="flex space-x-2">
                                        <button 
                                            onClick={() => navigate(`/patient/${patient.uid}`)}
                                            className="px-3 py-1 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors text-sm"
                                        >
                                            View Details
                                        </button>
                                        <button 
                                            onClick={() => handleMessagePatient(patient)}
                                            className="px-3 py-1 bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors text-sm"
                                        >
                                            Message
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </PageSection>

            {/* Quick Actions */}
            <PageSection title="Quick Actions" emoji="⚡">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button 
                        onClick={handleAddPatient}
                        className="p-4 bg-blue-100 hover:bg-blue-200 rounded-lg text-left transition-colors"
                    >
                        <div className="flex items-center space-x-3">
                            <span className="text-2xl">➕</span>
                            <div>
                                <div className="font-medium text-gray-900">Add New Patient</div>
                                <div className="text-sm text-gray-600">Register a new patient</div>
                            </div>
                        </div>
                    </button>
                    <button 
                        onClick={() => navigate('/analytics')}
                        className="p-4 bg-green-100 hover:bg-green-200 rounded-lg text-left transition-colors"
                    >
                        <div className="flex items-center space-x-3">
                            <span className="text-2xl">📊</span>
                            <div>
                                <div className="font-medium text-gray-900">View Analytics</div>
                                <div className="text-sm text-gray-600">Track patient progress</div>
                            </div>
                        </div>
                    </button>
                    <button 
                        onClick={() => navigate('/reports')}
                        className="p-4 bg-purple-100 hover:bg-purple-200 rounded-lg text-left transition-colors"
                    >
                        <div className="flex items-center space-x-3">
                            <span className="text-2xl">📋</span>
                            <div>
                                <div className="font-medium text-gray-900">Generate Report</div>
                                <div className="text-sm text-gray-600">Create patient reports</div>
                            </div>
                        </div>
                    </button>
                </div>
            </PageSection>
                </>
            )}

            {/* View Patient Modal */}
            {showViewModal && selectedPatient && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-2xl font-bold text-gray-900">Patient Details</h2>
                            <button 
                                onClick={closeModals}
                                className="text-gray-400 hover:text-gray-600 text-2xl"
                            >
                                ×
                            </button>
                        </div>
                        
                        <div className="space-y-4">
                            <div className="flex items-center space-x-4">
                                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                                    <span className="text-2xl">👤</span>
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900">{selectedPatient.displayName}</h3>
                                    <p className="text-gray-600">{selectedPatient.email}</p>
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h4 className="font-medium text-gray-900 mb-2">Status</h4>
                                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedPatient.status || 'Active')}`}>
                                        {selectedPatient.status || 'Active'}
                                    </span>
                                </div>
                                
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h4 className="font-medium text-gray-900 mb-2">Risk Level</h4>
                                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRiskColor(selectedPatient.riskLevel || 'Low')}`}>
                                        {selectedPatient.riskLevel || 'Low'} Risk
                                    </span>
                                </div>
                                
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h4 className="font-medium text-gray-900 mb-2">Progress</h4>
                                    <div className="flex items-center space-x-2">
                                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                                            <div 
                                                className="bg-blue-600 h-2 rounded-full" 
                                                style={{ width: `${selectedPatient.progress || 0}%` }}
                                            ></div>
                                        </div>
                                        <span className="text-sm font-medium">{selectedPatient.progress || 0}%</span>
                                    </div>
                                </div>
                                
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h4 className="font-medium text-gray-900 mb-2">Last Active</h4>
                                    <p className="text-sm text-gray-600">
                                        {selectedPatient.lastActive ? new Date(selectedPatient.lastActive).toLocaleDateString() : 'Unknown'}
                                    </p>
                                </div>
                            </div>
                            
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h4 className="font-medium text-gray-900 mb-2">Notes</h4>
                                <p className="text-sm text-gray-600">
                                    {selectedPatient.needsAttention ? 'This patient requires immediate attention and follow-up.' : 'Patient is stable and progressing well.'}
                                </p>
                            </div>
                        </div>
                        
                        <div className="flex justify-end space-x-3 mt-6">
                            <button 
                                onClick={closeModals}
                                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                            >
                                Close
                            </button>
                            <button 
                                onClick={() => {
                                    closeModals();
                                    handleMessagePatient(selectedPatient);
                                }}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                            >
                                Send Message
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Message Patient Modal */}
            {showMessageModal && selectedPatient && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-2xl font-bold text-gray-900">Send Message</h2>
                            <button 
                                onClick={closeModals}
                                className="text-gray-400 hover:text-gray-600 text-2xl"
                            >
                                ×
                            </button>
                        </div>
                        
                        <div className="space-y-4">
                            <div className="flex items-center space-x-3">
                                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                                    <span className="text-xl">👤</span>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-900">{selectedPatient.displayName}</h3>
                                    <p className="text-sm text-gray-600">{selectedPatient.email}</p>
                                </div>
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Subject
                                </label>
                                <input 
                                    type="text" 
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Enter message subject..."
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Message
                                </label>
                                <textarea 
                                    rows={4}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Type your message here..."
                                ></textarea>
                            </div>
                        </div>
                        
                        <div className="flex justify-end space-x-3 mt-6">
                            <button 
                                onClick={closeModals}
                                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={() => {
                                    alert(`Message sent to ${selectedPatient.displayName}!`);
                                    closeModals();
                                }}
                                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                            >
                                Send Message
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Patient Modal */}
            <AddPatientModal 
                isOpen={showAddPatientModal}
                onClose={closeModals}
                onPatientAdded={handlePatientAdded}
            />
        </PageContainer>
    );
};

export default TherapistPatients;
